// segmentDeepLink.js — the single source of truth for the user-facing segment
// deep-link (DESIGN-DECISIONS-LOG.md D-19, Gordon 2026-07-27, Option B).
//
// TWO distinct segment routes exist and must NEVER be conflated:
//   1. /wheel/segment/:id        — a bare standalone <Segment/> (wallpaper +
//      content only, no wheel/portrait/nav). This is the intentional React
//      Native mobile-WebView EMBED route (reads window.preloadedData). It is
//      LEFT UNTOUCHED and no in-app web affordance should point at it.
//   2. /wheel/<wheelId>?segment=<value>  — the user-facing DEEP-LINK: opens the
//      FULL ViewWheel (donut + portrait + nav) with that segment OPENED. This is
//      what every web share/open-segment affordance links to.
//
// Keeping both the builder (affordances) and the resolver (ViewWheel) here means
// the URL shape can only ever change in one place.

/**
 * Build the user-facing segment deep-link PATH (relative — no origin).
 * Falls back to the embed route ONLY when a wheelId is genuinely unavailable,
 * so a missing id degrades to today's behaviour rather than a broken link.
 *
 * @param {number|string} wheelId   the parent wheel's id
 * @param {number|string} segmentId the segment (activity) id
 * @returns {string} e.g. "/wheel/42?segment=1877"
 */
export function buildSegmentDeepLinkPath(wheelId, segmentId) {
  const seg = encodeURIComponent(String(segmentId));
  if (wheelId == null || String(wheelId) === "") {
    // No parent wheel context — degrade to the legacy embed route.
    return `/wheel/segment/${seg}`;
  }
  return `/wheel/${encodeURIComponent(String(wheelId))}?segment=${seg}`;
}

/**
 * Resolve a `?segment=<value>` query param to the id of the segment ViewWheel
 * should open. Pure — takes the loaded wheel segments and the raw param.
 *
 * Match order:
 *   1. by segment id (what every in-app affordance emits) — exact, robust to
 *      reordering.
 *   2. fallback: a human-typed 1-based position into the wheel's segment order
 *      (so /wheel/42?segment=2 opens the 2nd segment when no id 2 exists).
 *
 * @param {Array<{id:*, position?:number}>} segments loaded wheel segments
 * @param {string|number|null|undefined} raw the raw `?segment=` value
 * @returns {*|null} the id to open, or null if it resolves to nothing
 */
export function resolveDeepLinkSegment(segments, raw) {
  if (!Array.isArray(segments) || segments.length === 0) return null;
  if (raw == null) return null;
  const value = String(raw).trim();
  if (value === "") return null;

  // 1. Exact segment-id match (primary — affordances emit the id).
  const byId = segments.find((s) => s && s.id != null && String(s.id) === value);
  if (byId) return byId.id;

  // 2. Positional fallback: 1-based index into position-sorted segments.
  const n = Number(value);
  if (Number.isInteger(n) && n >= 1 && n <= segments.length) {
    const sorted = [...segments].sort(
      (a, b) => (a?.position ?? 0) - (b?.position ?? 0)
    );
    const atPos = sorted[n - 1];
    if (atPos && atPos.id != null) return atPos.id;
  }

  return null;
}
